import { useState } from "react";
import { TRACKING_ID_PLACEHOLDER } from "../utils/constants";
import { TrackingIdInclusionState } from "../types/ad-asset-group";

interface UseTrackingIdInclusionProps {
  initialState?: Partial<TrackingIdInclusionState>;
  onFieldChange: (field: string, value: string) => void;
}

export function useTrackingIdInclusion({ initialState, onFieldChange }: UseTrackingIdInclusionProps) {
  const [trackingIdInclusion, setTrackingIdInclusion] = useState<TrackingIdInclusionState>({
    google_destination_url: true,
    facebook_destination_url: true,
    msan_destination_url: true,
    name: true,
    ...initialState
  });

  // Handler for destination URL changes with optional tracking ID
  const handleDestinationUrlChange = (field: keyof TrackingIdInclusionState, newValue: string) => {
    if (trackingIdInclusion[field]) {
      // If tracking ID should be included, use the protected behavior
      const protectedStringIndex = newValue.indexOf(TRACKING_ID_PLACEHOLDER);
      if (protectedStringIndex === -1) {
        onFieldChange(field, TRACKING_ID_PLACEHOLDER);
        return;
      }
    }
    // Otherwise, allow normal editing
    onFieldChange(field, newValue);
  };

  // Handler for tracking ID inclusion checkbox changes
  const handleTrackingIdInclusionChange = (
    field: keyof TrackingIdInclusionState,
    included: boolean,
    currentValue: string
  ) => {
    setTrackingIdInclusion((prev) => ({ ...prev, [field]: included }));

    if (included) {
      // If now including tracking ID, add it to the field value if not present
      if (!currentValue.includes(TRACKING_ID_PLACEHOLDER)) {
        onFieldChange(field, currentValue ? `${currentValue}${TRACKING_ID_PLACEHOLDER}` : TRACKING_ID_PLACEHOLDER);
      }
    } else {
      // If no longer including tracking ID, remove it from the field value
      const cleanedValue = currentValue.replace(TRACKING_ID_PLACEHOLDER, "").trim();
      onFieldChange(field, cleanedValue);
    }
  };

  return {
    trackingIdInclusion,
    handleDestinationUrlChange,
    handleTrackingIdInclusionChange
  };
}
